<template>
  <footer class="footer">
    <div class="footer-glow"></div>
    <div class="page footer-inner">
      <div class="f-main">
        <div class="f-brand">
          <BrandLogo size="md"/>
          <p class="f-slogan">{{ company.slogan }}</p>
          <p class="f-intro">{{ company.intro }}</p>
          <div class="f-tags">
            <!-- 由 businessLines 派生，避免新增业务线后这里忘记同步 -->
            <span v-for="l in businessLines" :key="l.key" class="tag">{{ l.name }}</span>
          </div>
        </div>

        <div class="f-col">
          <h4>业务线</h4>
          <router-link v-for="l in businessLines" :key="l.key" :to="l.path">{{ l.name }}</router-link>
        </div>

        <div class="f-col">
          <h4>关于</h4>
          <router-link to="/about">公司介绍</router-link>
          <router-link to="/about#history">发展历程</router-link>
          <router-link to="/insights">行业洞察</router-link>
          <router-link to="/contact">联系我们</router-link>
          <a :href="demo.url" target="_blank" rel="noopener noreferrer">在线演示</a>
          <router-link to="/privacy">隐私政策</router-link>
          <router-link to="/terms">服务条款</router-link>
          <router-link to="/home">首页</router-link>
        </div>

        <div class="f-col f-contact">
          <h4>联系方式</h4>
          <a :href="`tel:${company.phone}`"><IconSvg name="phone"/> {{ company.phone }}</a>
          <p class="f-wx"><IconSvg name="users2"/> {{ company.wechat.label }}：{{ company.wechat.search }}</p>
          <a :href="`mailto:${company.email}`"><IconSvg name="mail"/> {{ company.email }}</a>
          <a class="f-addr" :href="amapUrl" target="_blank" rel="noopener noreferrer">
            <IconSvg name="location"/> {{ company.address }}
          </a>
        </div>
      </div>

      <div class="f-bottom">
        <span>© {{ year }} {{ company.name }} 版权所有</span>
        <a class="f-icp" :href="company.icpUrl" target="_blank" rel="noopener noreferrer">{{ company.icp }}</a>
      </div>
    </div>
  </footer>
</template>

<script>
import BrandLogo from '@/components/common/BrandLogo'
import IconSvg from '@/components/common/IconSvg'
import { businessLines, company, demo } from '@/data/site'

export default {
  name: 'SiteFooter',
  components: { BrandLogo, IconSvg },
  data() {
    return {
      businessLines,
      company,
      demo,
      year: new Date().getFullYear(),
      amapUrl: `https://www.amap.com/search?query=${encodeURIComponent(company.address)}`
    }
  }
}
</script>

<style lang="scss" scoped>
.footer {
  position: relative;
  margin-top: 40px;
  padding: 66px 0 26px;
  border-top: 1px solid var(--line);
  background: linear-gradient(180deg, rgba(5, 12, 27, 0.6), rgba(3, 7, 17, 0.98));
  overflow: hidden;
}

.footer-glow {
  position: absolute;
  top: -140px;
  left: 50%;
  width: 620px;
  height: 280px;
  margin-left: -310px;
  background: radial-gradient(closest-side, rgba(34, 224, 255, 0.22), transparent 70%);
  filter: blur(30px);
  pointer-events: none;
}

.footer-inner { position: relative; }

.f-main {
  display: grid;
  grid-template-columns: 1.6fr 0.8fr 0.8fr 1.1fr;
  gap: 34px;
  padding-bottom: 40px;
}

.f-slogan {
  margin-top: 18px;
  font-size: 15px;
  color: var(--cyan);
  letter-spacing: 0.06em;
}

.f-intro {
  margin-top: 10px;
  font-size: 13.5px;
  line-height: 1.9;
  color: var(--txt-3);
  max-width: 380px;
}

.f-tags { display: flex; gap: 8px; margin-top: 18px; flex-wrap: wrap; }

.f-col {
  display: flex;
  flex-direction: column;
  gap: 6px;

  /* 页脚文字链接本身是触点，给足纵向高度（实测 ≥34px） */
  > a {
    display: inline-flex;
    align-items: center;
    min-height: 34px;
    width: 100%;
  }

  h4 {
    font-size: 14px;
    letter-spacing: 0.14em;
    color: var(--txt);
    margin-bottom: 4px;
  }

  a {
    font-size: 14px;
    color: var(--txt-3);
    transition: color 0.3s, transform 0.3s;
    &:hover { color: var(--cyan); transform: translateX(2px); }
  }
}

/* 移动端：页脚链接加大触控区域（≥36px） */
@media (max-width: 720px) {
  .f-col {
    gap: 2px;
    a { padding: 8px 0; }
  }
}

.f-contact {
  /* 图标尺寸用 --ico 显式给定：IconSvg 的 scoped `.ico` 会压过 `.f-contact svg` 这类外层规则 */
  a, .f-addr, .f-wx {
    --ico: 16px;
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 14px;
    color: var(--txt-3);

    svg { color: var(--cyan); flex-shrink: 0; }
  }

  a:hover { color: var(--cyan); }
  .f-addr { align-items: flex-start; line-height: 1.7; margin: 0; }
  .f-wx { margin: 0; }
}

.f-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding-top: 22px;
  border-top: 1px solid rgba(120, 176, 255, 0.1);
  font-size: 13px;
  color: var(--txt-3);

  /* 备案号按规范可点击跳转工信部备案查询；补足竖向点击区域（≥24px） */
  .f-icp { padding: 3px 0; transition: color 0.3s; }
  .f-icp:hover { color: var(--cyan); }
}

@media (max-width: 980px) {
  .f-main { grid-template-columns: 1fr 1fr; gap: 28px; }
}

@media (max-width: 620px) {
  .footer { padding-top: 48px; }
  .f-main { grid-template-columns: 1fr; }
  .f-bottom { flex-direction: column; align-items: flex-start; }
}
</style>
